import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { Search, X } from "lucide-react";
import { api } from "../../../redux/apis/api";
import { billedItemBatchesApi } from "../../../redux/apis/invoice-api";
import errorToast from "../../helper-components/error-toast";

const SEARCH_DEBOUNCE_MS = 350;
const MIN_SEARCH_LENGTH = 2;
const PHONE_MAX_LENGTH = 15;
const EMPTY_DRAFT = { item: null, batch: null, minQty: "", phone: "" };

const LABEL_CLASS =
  "text-[length:var(--bill-item-search-label-fs)] font-[var(--bill-item-search-label-fw)] text-[var(--bill-item-search-label-fg)]";
const INPUT_CLASS =
  "h-[32px] w-full border border-[var(--head-input-border-color)] bg-[var(--head-input-bg-color)] px-[8px] border-radius text-[var(--head-input-fg-color)] text-[length:var(--head-input-font-size)] font-[var(--head-input-font-weight)] placeholder:text-[var(--head-input-placeholder-color)] focus:outline-none focus:border-[var(--head-input-focus-border-color)] disabled:cursor-not-allowed disabled:bg-[var(--header-button-disabled-bg-color)]";
const SEARCH_BUTTON_CLASS =
  "flex h-[32px] items-center justify-center gap-[8px] px-[12px] whitespace-nowrap border-radius transition duration-300 ease-in-out text-[length:var(--primary-button-font-size)] font-[var(--primary-button-font-weight)] bg-[var(--primary-button-bg-color)] text-[var(--primary-button-fg-color)] hover:bg-[var(--primary-button-hover-color)] disabled:cursor-not-allowed disabled:bg-[var(--header-button-disabled-bg-color)] disabled:text-[var(--header-button-disabled-fg-color)]";
const CLEAR_BUTTON_CLASS =
  "flex h-[32px] items-center gap-[8px] px-[12px] whitespace-nowrap border-radius border border-[var(--header-button-border-color)] bg-[var(--header-button-bg-color)] text-[var(--header-button-fg-color)] hover:bg-[var(--header-button-hover-color)] text-[length:var(--head-button-font-size)] font-[var(--head-button-font-weight)]";

// react-select sized like the filter row's 32px inputs
const SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    borderRadius: 6,
    borderColor: state.isFocused
      ? "var(--head-input-focus-border-color)"
      : "var(--head-input-border-color)",
    backgroundColor: state.isDisabled
      ? "var(--header-button-disabled-bg-color)"
      : "var(--head-input-bg-color)",
    boxShadow: "none",
    fontSize: "var(--head-input-font-size)",
    "&:hover": { borderColor: "var(--head-input-focus-border-color)" },
  }),
  valueContainer: (base) => ({ ...base, height: 30, padding: "0 8px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: 30 }),
  indicatorSeparator: () => ({ display: "none" }),
  placeholder: (base) => ({ ...base, color: "var(--head-input-placeholder-color)" }),
  singleValue: (base) => ({ ...base, color: "var(--head-input-fg-color)" }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    fontSize: "var(--head-input-font-size)",
    color: "var(--head-input-fg-color)",
    backgroundColor: state.isSelected
      ? "var(--bill-item-search-option-selected-bg)"
      : state.isFocused
      ? "var(--bill-item-search-option-hover-bg)"
      : "var(--head-input-bg-color)",
  }),
};

const toItemOption = (item) => ({
  value: item.item_id,
  label: [item.item_code, item.item_name].filter(Boolean).join(" — "),
});

// Items whose name or code matches (/product/get searches one field at a time)
const searchItems = async (text) => {
  const results = await Promise.allSettled(
    ["item_name", "item_code"].map((field) =>
      api.get("/product/get", { params: { field, value: text } })
    )
  );
  if (results.every((result) => result.status === "rejected")) {
    errorToast("Couldn't search the items");
    return [];
  }
  const optionsById = new Map();
  results
    .filter((result) => result.status === "fulfilled")
    .flatMap((result) => result.value?.data?.data ?? [])
    .forEach((item) => {
      if (item?.item_id && !optionsById.has(item.item_id)) {
        optionsById.set(item.item_id, toItemOption(item));
      }
    });
  return [...optionsById.values()];
};

// The form's fields for an applied search (filterData.itemSearch)
const toDraft = (search) =>
  search
    ? {
        item: search.item_id ? { value: search.item_id, label: search.item_label } : null,
        batch: search.batch ? { value: search.batch, label: search.batch } : null,
        minQty: search.min_qty ?? "",
        phone: search.phone ?? "",
      }
    : EMPTY_DRAFT;

// The batches the item has been billed in
const useBilledBatches = (itemId) => {
  const [state, setState] = useState({ options: [], isLoading: false });
  useEffect(() => {
    if (!itemId) {
      setState({ options: [], isLoading: false });
      return undefined;
    }
    let cancelled = false;
    setState({ options: [], isLoading: true });
    billedItemBatchesApi(itemId)
      .then((res) => {
        if (cancelled) return;
        const batches = res?.data?.data ?? [];
        setState({ options: batches.map((batch) => ({ value: batch, label: batch })), isLoading: false });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ options: [], isLoading: false });
        errorToast("Couldn't load the item's batches");
      });
    return () => {
      cancelled = true;
    };
  }, [itemId]);
  return state;
};

const Field = ({ htmlFor, label, className, children }) => (
  <div className={`flex flex-col gap-1 ${className}`}>
    <label htmlFor={htmlFor} className={LABEL_CLASS}>
      {label}
    </label>
    {children}
  </div>
);

// The Billings list's item search: bills with an item (optionally of one of
// its billed batches, and at least a quantity of it) and / or for a phone
// number. value is the applied search (filterData.itemSearch).
const BillingItemSearch = ({ value, onApply, onClear }) => {
  const [draft, setDraft] = useState(() => toDraft(value));
  const searchTimerRef = useRef(null);
  const batches = useBilledBatches(draft.item?.value);

  // Clear All on the filter row drops the applied search: start over too
  useEffect(() => {
    if (!value) setDraft(EMPTY_DRAFT);
  }, [value]);

  useEffect(() => () => clearTimeout(searchTimerRef.current), []);

  const updateDraft = (changes) => setDraft((previous) => ({ ...previous, ...changes }));

  const loadItemOptions = (inputValue, callback) => {
    clearTimeout(searchTimerRef.current);
    const text = inputValue.trim();
    if (text.length < MIN_SEARCH_LENGTH) {
      callback([]);
      return;
    }
    searchTimerRef.current = setTimeout(async () => callback(await searchItems(text)), SEARCH_DEBOUNCE_MS);
  };

  const canSearch = Boolean(draft.item || draft.phone);

  const applySearch = (event) => {
    event.preventDefault();
    if (!canSearch) return;
    onApply({
      item_id: draft.item?.value ?? "",
      item_label: draft.item?.label ?? "",
      batch: draft.item ? draft.batch?.value ?? "" : "",
      min_qty: draft.item ? draft.minQty : "",
      phone: draft.phone,
    });
  };

  const clearSearch = () => {
    setDraft(EMPTY_DRAFT);
    onClear();
  };

  return (
    <div className="px-[24px] pb-[16px]">
      <form
        aria-label="Search bills by item"
        onSubmit={applySearch}
        className="flex flex-wrap items-end gap-3 border border-[var(--bill-item-search-border)] bg-[var(--bill-item-search-bg)] p-3 border-radius"
      >
        <Field htmlFor="bill-search-item" label="Item name / code" className="w-full sm:w-[24rem]">
          <AsyncSelect
            inputId="bill-search-item"
            cacheOptions
            isClearable
            loadOptions={loadItemOptions}
            value={draft.item}
            onChange={(item) => updateDraft({ item, batch: null, minQty: item ? draft.minQty : "" })}
            placeholder="Type the item name or code"
            noOptionsMessage={({ inputValue }) =>
              inputValue.trim().length < MIN_SEARCH_LENGTH
                ? `Type at least ${MIN_SEARCH_LENGTH} characters`
                : "No items found"
            }
            styles={SELECT_STYLES}
            menuPortalTarget={document.body}
          />
        </Field>

        <Field htmlFor="bill-search-batch" label="Batch (optional)" className="w-[11rem]">
          <Select
            inputId="bill-search-batch"
            isClearable
            isDisabled={!draft.item}
            isLoading={batches.isLoading}
            options={batches.options}
            value={draft.batch}
            onChange={(batch) => updateDraft({ batch })}
            placeholder={draft.item ? "Any batch" : "Pick an item first"}
            noOptionsMessage={() => "No billed batches"}
            styles={SELECT_STYLES}
            menuPortalTarget={document.body}
          />
        </Field>

        <Field htmlFor="bill-search-qty" label="Qty (at least)" className="w-[8rem]">
          <input
            id="bill-search-qty"
            type="number"
            min={1}
            step={1}
            disabled={!draft.item}
            value={draft.minQty}
            onChange={(event) => updateDraft({ minQty: event.target.value })}
            onKeyDown={(event) => {
              // whole quantities only
              if (["-", "e", "."].includes(event.key)) event.preventDefault();
            }}
            placeholder="Any qty"
            className={`${INPUT_CLASS} input-field-no-spin-buttons`}
          />
        </Field>

        <Field htmlFor="bill-search-phone" label="Phone number" className="w-[11rem]">
          <input
            id="bill-search-phone"
            type="tel"
            inputMode="numeric"
            maxLength={PHONE_MAX_LENGTH}
            value={draft.phone}
            onChange={(event) => updateDraft({ phone: event.target.value.replace(/\D/g, "") })}
            placeholder="Any phone"
            className={INPUT_CLASS}
          />
        </Field>

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={!canSearch}
            title={canSearch ? "Search the bills" : "Pick an item or enter a phone number"}
            className={SEARCH_BUTTON_CLASS}
          >
            <Search size={14} aria-hidden="true" />
            Search
          </button>
          <button type="button" onClick={clearSearch} className={CLEAR_BUTTON_CLASS}>
            <X size={14} aria-hidden="true" />
            Clear
          </button>
        </div>
      </form>
    </div>
  );
};

export default BillingItemSearch;
